<template>
	<PublicLayoutTwo
		:title="$t('Home')"
		:lcp-image="firstHeroImage"
		:preload-images="heroImages"
	>
		<HomeHero />
		<section
			class="relative overflow-hidden bg-home-world-map bg-cover bg-bottom bg-no-repeat pb-56 md:pb-[4.5rem]"
		>
			<div class="absolute -inset-px bg-gradient-to-b from-white to-white/0" />
			<div
				class="sp-padding-y-md sp-container relative flex flex-col gap-x-8 gap-y-12 lg:flex-row"
			>
				<h3 class="w-full text-4xl 2xl:text-5xl">
					{{ $t('Connect, build, and launch exceptional products in no time') }}
				</h3>
				<div class="w-full">
					<p class="mb-8 text-sm font-medium uppercase">
						{{ $t('Continuously evolving talent, ready today!') }}
					</p>
					<div class="flex flex-col gap-y-8 md:flex-row">
						<div
							v-for="stat in statistics"
							:key="stat.boldText"
							class="border-l-4 border-primary px-4 py-1"
						>
							<h5 class="mb-1 text-4xl 2xl:text-5xl">{{ stat.title }}</h5>
							<p>
								<span class="font-bold">{{ stat.boldText }}{{ ' ' }}</span
								>{{ stat.description }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</section>
		<div class="absolute z-50 -mt-56 w-full overflow-hidden md:-mt-20">
			<div class="sp-container mx-auto overflow-visible">
				<TrustedBySlider />
			</div>
		</div>
		<LazyLoad>
			<div class="relative z-20">
				<EmpowerTeamSection />
			</div>
		</LazyLoad>
		<LazyLoad>
			<div class="relative bg-slate-100">
				<div id="how-it-works" class="relative z-50 -mt-32">
					<FindAndHireSection />
				</div>
			</div>
		</LazyLoad>
		<LazyLoad>
			<ScalingAdvantagesSection />
		</LazyLoad>
		<LazyLoad>
			<HowDoWeVetSection :cards="cards" :steps="steps" />
		</LazyLoad>
		<LazyLoad>
			<PartnersList />
		</LazyLoad>
		<LazyLoad>
			<TopTierTech />
		</LazyLoad>
		<LazyLoad>
			<Testimonials />
		</LazyLoad>
	</PublicLayoutTwo>
</template>

<script setup lang="ts">
import HomeHero from './Partials/Home/HomeHero/HomeHero.vue';
import { cardsData } from './Partials/Home/HomeHero/section-data';
import { cards, steps } from './Partials/HowDoWeVetSection/section-data';
import { defineAsyncComponent } from 'vue';
import LazyLoad from '@/Components/LazyLoad.vue';
import TrustedBySlider from '@/Components/TrustedBySlider/TrustedBySlider.vue';
import PublicLayoutTwo from '@/Layouts/PublicLayoutTwo.vue';

// Get hero images for preload (first image is LCP, others are for carousel)
const firstHeroImage = cardsData[0]?.image || null;
// Preload first 2-3 carousel images for smooth transitions
const heroImages = cardsData
	.slice(1, 3)
	.map((card) => card.image)
	.filter(Boolean);
// Lazy load components that are below the fold for better initial load performance
const EmpowerTeamSection = defineAsyncComponent(
	() => import('./Partials/Home/EmpowerTeamSection/EmpowerTeamSection.vue'),
);
const FindAndHireSection = defineAsyncComponent(
	() => import('./Partials/Home/FindAndHireSection/FindAndHireSection.vue'),
);
const ScalingAdvantagesSection = defineAsyncComponent(
	() =>
		import(
			'./Partials/Home/ScalingAdvantagesSection/ScalingAdvantagesSection.vue'
		),
);
const Testimonials = defineAsyncComponent(
	() => import('./Partials/Home/Testimonials.vue'),
);
const TopTierTech = defineAsyncComponent(
	() => import('./Partials/Home/TopTierTech.vue'),
);
const HowDoWeVetSection = defineAsyncComponent(
	() => import('./Partials/HowDoWeVetSection/HowDoWeVetSection.vue'),
);
const PartnersList = defineAsyncComponent(
	() => import('./Partials/PartnersListSection/PartnersList.vue'),
);

const statistics = [
	{
		title: '5000+',
		boldText: 'Professionals',
		description: 'available in scaling network',
	},
	{
		title: '55+',
		boldText: 'Technologies and skills',
		description: 'available across our team',
	},
	{
		title: '10+',
		boldText: 'Years in tech business',
		description: 'creating innovative solutions',
	},
	{
		title: '10+',
		boldText: 'Countries',
		description: 'listed across all professionals',
	},
] as const;
</script>
